// Compute which squapi workspaces a feature is available in — from the
// PRIMARY flag only (flag_ids[0]). Secondary flags are supporting context
// on the feature; the badge, hover, and auto-derived announcement state
// all reflect just the primary.
//
// Primary-flag rules:
//   - globalIsDisabled → no workspaces
//   - workspaces.includes("*") → all live workspaces EXCEPT disallowed
//   - otherwise → the explicit `workspaces` list, orphan-filtered
//
// (Server-side flags are already sanitized against squapi's live
// workspace list, so orphan filtering here is defensive.)
import type {
  FeatureFlag,
  RolloutKind,
  WorkspaceClassification,
  WorkspaceInfo,
} from "@release-hub/shared";

export interface EffectiveWorkspaces {
  /** Workspace IDs where the primary flag has the feature enabled. */
  ids: string[];
  /** True when the primary flag is wildcarded with no disallow list —
   *  functionally "everyone gets it". UI can short-circuit to "All". */
  isEveryone: boolean;
}

export function effectiveWorkspaces(
  flags: FeatureFlag[],
  allWorkspaces: WorkspaceInfo[],
): EffectiveWorkspaces {
  const primary = flags[0];
  if (!primary) return { ids: [], isEveryone: false };
  if (primary.globalIsDisabled) return { ids: [], isEveryone: false };

  const knownIds = new Set(allWorkspaces.map((w) => w.id));
  if (primary.workspaces.includes("*")) {
    const disallowed = new Set(primary.disallowedWorkspaces);
    const ids = [...knownIds].filter((id) => !disallowed.has(id));
    return { ids, isEveryone: disallowed.size === 0 };
  }
  const ids = primary.workspaces.filter((id) => knownIds.has(id));
  return { ids, isEveryone: false };
}

/** Bucketed view of a workspace list, split by squapi's classification
 *  taxonomy. "External" = revenue customers (what SEs care about);
 *  "internal" = internal test workspaces; "partner" = investors/press;
 *  "selfServe" = self-serve trials;
 *  "unclassified" = workspaces that never got a classification set. */
export interface ClassifiedWorkspaces {
  external: WorkspaceInfo[]; // revenue
  internal: WorkspaceInfo[]; // internal + internalImportant
  partner: WorkspaceInfo[]; // partner
  selfServe: WorkspaceInfo[]; // selfServe
  unclassified: WorkspaceInfo[]; // classification === null
}

const EXTERNAL: WorkspaceClassification = "revenue";
const PARTNER: WorkspaceClassification = "partner";

export function groupWorkspacesByClassification(
  ws: WorkspaceInfo[],
): ClassifiedWorkspaces {
  const result: ClassifiedWorkspaces = {
    external: [],
    internal: [],
    partner: [],
    selfServe: [],
    unclassified: [],
  };
  for (const w of ws) {
    switch (w.classification) {
      case EXTERNAL:
        result.external.push(w);
        break;
      case "internal":
      case "internalImportant":
        result.internal.push(w);
        break;
      case PARTNER:
        result.partner.push(w);
        break;
      case "selfServe":
        result.selfServe.push(w);
        break;
      default:
        result.unclassified.push(w);
    }
  }
  return result;
}

// ─── External-facing rollout tier ────────────────────────────────────
//
// The ladder a *feature launch* advances along, keyed off EXTERNAL
// (revenue) workspace coverage of the primary flag — deliberately
// distinct from flagRollout(), which counts all workspaces:
//
//   in_development — 0 external workspaces have it (internal/partner only)
//   ea             — some, but not all, external workspaces have it
//   ga             — every external workspace has it (clean wildcard, or
//                    "GA except N" where none of the N disallowed are external)
//
// Partner/self-serve/internal/unclassified workspaces never move the tier. When there
// are no external workspaces in the universe at all, a wildcard still reads
// as GA (nothing external is being withheld).

export type FeatureTierKind = Extract<RolloutKind, "in_development" | "ea" | "ga">;

export interface FeatureTier {
  kind: FeatureTierKind;
  /** External workspaces that have the feature. */
  externalEnabled: number;
  /** External workspaces in the universe (the denominator). */
  externalTotal: number;
  /** Short uppercase-ready label, e.g. "In-Development", "EA", "GA". */
  label: string;
}

const TIER_LABEL: Record<FeatureTierKind, string> = {
  in_development: "Not yet available",
  ea: "EA",
  ga: "GA",
};

export function featureTier(
  flags: FeatureFlag[],
  allWorkspaces: WorkspaceInfo[],
): FeatureTier {
  const { external: externalUniverse } = groupWorkspacesByClassification(allWorkspaces);
  const externalTotal = externalUniverse.length;
  const externalIds = new Set(externalUniverse.map((w) => w.id));

  const eff = effectiveWorkspaces(flags, allWorkspaces);
  const externalEnabled = eff.isEveryone
    ? externalTotal
    : eff.ids.filter((id) => externalIds.has(id)).length;

  let kind: FeatureTierKind;
  if (externalEnabled === 0) {
    // No external reach → In-Development, UNLESS a wildcard is live and there
    // simply are no external workspaces to reach (then it's fully GA).
    kind = eff.isEveryone && externalTotal === 0 ? "ga" : "in_development";
  } else if (externalTotal > 0 && externalEnabled >= externalTotal) {
    kind = "ga";
  } else {
    kind = "ea";
  }

  return { kind, externalEnabled, externalTotal, label: TIER_LABEL[kind] };
}
